import { z } from 'zod'
import { Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'
import { useUserStore } from '@/stores/user'
import { timeNotation, timeHHMM } from '@/lib/utils/time-notation'

import { graphql } from '@/gql'
import type { Coassign_Attendance_Insert_Input } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { type RawBuilder, sql } from 'kysely'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import i18next from 'i18next'

// ヘルパー: 言語に応じたメッセージを取得
// Helper: Get message based on current language
const getMessage = (key: string): string => {
  return i18next.t(`validation.attendance.${key}`) as string
}

function json<T>(value: T): RawBuilder<T> {
  return sql`${JSON.stringify(value)}`
}

export const toInt = (s: string) => Number(s.replace(':', ''))

const isLessThan3600 = (v: string) => Number(v.split(':')[0]) < 36

const getLessThan3600Message = () => ({ message: getMessage('lessThan3600') })

const breakSchema = z.object({
  start: timeNotation.refine(isLessThan3600, getLessThan3600Message()),
  end: timeNotation.refine(isLessThan3600, getLessThan3600Message())
})

interface Break {
  start: string
  end: string
}

const isOverlap = (brk: Break, targetTime: string) => {
  return toInt(brk.start) < toInt(targetTime) && toInt(targetTime) < toInt(brk.end)
}

const clockSchema = z
  .object({
    start: timeNotation.refine(isLessThan3600, getLessThan3600Message()).default(timeHHMM('10:00')),
    end: timeNotation.refine(isLessThan3600, getLessThan3600Message()).default(timeHHMM('19:00')),
    breaks: z.array(breakSchema).default([])
  })
  .refine(({ start, end }) => toInt(start) < toInt(end), {
    message: getMessage('startBeforeEnd'),
    path: ['start']
  })
  .superRefine(({ start, end, breaks }, ctx) => {
    Object.entries(breaks).forEach(([key, brk]) => {
      if (brk.start === undefined || brk.end === undefined) return
      if (toInt(brk.end) < toInt(brk.start))
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_date,
          message: getMessage('startBeforeEnd'),
          path: ['breaks', key, 'start']
        })
    })

    Object.entries(breaks).forEach(([key, brk]) => {
      // |- - 休憩開始 - -|▼出勤-------------退勤▼|- - 休憩開始 - -|
      if (brk.start !== undefined)
        if (toInt(brk.start) < toInt(start) || toInt(end) < toInt(brk.start))
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_date,
            message: getMessage('withinWorkingHours'),
            path: ['breaks', key, 'start']
          })
      // |- - 休憩終了 - -|▼出勤-------------退勤▼|- - 休憩終了 - -|
      if (brk.end !== undefined)
        if (toInt(brk.end) < toInt(start) || toInt(end) < toInt(brk.end))
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_date,
            message: getMessage('withinWorkingHours'),
            path: ['breaks', key, 'end']
          })
    })

    const breaks_: Break[] = []
    Object.entries(breaks).forEach(([key, brk]) => {
      if (brk.start === undefined || brk.end === undefined) return
      //       ┌─brk_.start────休憩期間──brk_.end──┐
      //                          └─brk.start──休憩期間────────────
      const isStartInvalid = breaks_.some((existingBreak) => isOverlap(existingBreak, brk.start))
      if (isStartInvalid) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_date,
          message: getMessage('overlapBreak'),
          path: ['breaks', key, 'start']
        })
      }
      //       ┌─brk_.start────休憩期間──brk_.end──┐
      //                          └─brk.start──休憩期間────────────
      const isEndInvalid = breaks_.some((existingBreak) => isOverlap(existingBreak, brk.end))
      if (isEndInvalid) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_date,
          message: getMessage('overlapBreak'),
          path: ['breaks', key, 'end']
        })
      }
      breaks_.push(brk) // push for next forEach loop
    })
  })

export const attendanceSchema = z.object({
  id: z.string().length(21),
  // contextual values
  user_id: z.string().length(21),
  month: z.string().regex(/[0-9]{4}-[0-9]{2}-[0-9]{2}/),
  // can modifies
  clocks: z.record(clockSchema)
})

export type Attendance = z.input<typeof attendanceSchema>

export class AttendanceMutator extends Mutator<typeof attendanceSchema> {
  static readonly stageableClassName = 'AttendanceMutator'
  schema = attendanceSchema
  table = 'attendance' as const

  static async load(
    id: string,
    context: Pick<Attendance, 'user_id' | 'month'>
  ): Promise<Reactive<AttendanceMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const record = await DataBase.selectFrom('attendance')
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    if (record) {
      self.base = {
        ...context,
        ...{ id },
        month: context['month'],
        clocks: record.clocks
      }
    }
    self.context = { ...context, ...{ id } }
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const deleteClocks = Object.entries(this.modified.clocks ?? {})
      .filter(([, clock]) => clock?.start === undefined && clock?.end === undefined)
      .map(([date]) => date)
    const sqliteParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => {
        if (key === 'clocks' && typeof value === 'object') {
          deleteClocks.forEach((key) => {
            delete value[key]
          })
          return [key, json(value)]
        }
        return [key, typeof value === 'boolean' ? Number(value) : value]
      })
    )
    return [DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile()]
  }

  buildInsertParams(params: z.output<typeof attendanceSchema>) {
    const tenant_id = useUserStore().userInfo!.tenantId
    return {
      tenant_id,
      id: params.id,
      user_id: params.user_id,
      month: params.month,
      clocks: json(params.clocks)
    }
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const params = (await this.parsed)!
    const sqliteParams = this.buildInsertParams(params)
    return [DataBase.insertInto(this.table).values(sqliteParams).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    throw Error('attendance can not delete')
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const gqlparams = {
      id: params.id,
      user_id: params.user_id,
      month: params.month,
      clocks: params.clocks
    }
    const variables: Coassign_Attendance_Insert_Input = { ...gqlparams, ...{ commit_id } }
    return [{ mutation: insertAttendanceMutationDocument, variables: { input: variables } }]
  }

  /*
   * DBレコードが月単位なので、jsonbフィールドを部分的に書き換える
   */
  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const params = (await this.parsed)!
    const variables = { id, commit_id, clocks: params.clocks }
    const deleteClocks = Object.entries(this.modified.clocks ?? {})
      .filter(([, clock]) => clock?.start === undefined && clock?.end === undefined)
      .map(([date]) => date)
    const deleteMutations = deleteClocks.map((clockDate) => ({
      mutation: removeClocksFromAttendanceMutationDocument,
      variables: { id, commit_id, clocks: [clockDate] } // Assuming each mutation requires an array of clocks
    }))
    return [...deleteMutations, { mutation: updateAttendanceMutationDocument, variables }]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    throw Error('attendance can not delete')
  }
}

const insertAttendanceMutationDocument = graphql(/* GraphQL */ `
  mutation insertAttendance($input: coassign_attendance_insert_input!) {
    insert_coassign_attendance_one(object: $input) {
      created_at
    }
  }
`)

const updateAttendanceMutationDocument = graphql(/* GraphQL */ `
  mutation updateAttendance($id: String!, $commit_id: String!, $clocks: jsonb!) {
    update_coassign_attendance_by_pk(
      pk_columns: { id: $id }
      _set: { commit_id: $commit_id }
      _append: { clocks: $clocks }
    ) {
      updated_at
    }
  }
`)

const removeClocksFromAttendanceMutationDocument = graphql(/* GraphQL */ `
  mutation removeClocksFromAttendance($id: String!, $commit_id: String!, $clocks: [String!]!) {
    update_coassign_attendance_by_pk(
      pk_columns: { id: $id }
      _set: { commit_id: $commit_id }
      _delete_at_path: { clocks: $clocks }
    ) {
      updated_at
    }
  }
`)

// const updateAttendanceMutationDocument = graphql(/* GraphQL */ `
//   mutation updateAttendance($id: String!, $clocks: jsonb!) {
//     update_coassign_attendance(where: { id: { _eq: $id } }, _append: { clocks: $clocks }) {
//       returning {
//         clocks
//       }
//     }
//   }
// `)
